/* eslint-disable max-classes-per-file */

import { SPACE } from './constants'

class BaseError extends Error {
  name: string
  constructor(message = SPACE) {
    super(message || 'BaseError')
    this.name = 'Nugs.net SDK Error' // (2)
  }
}

export class NotImplemented extends BaseError {
  name = 'NotImplemented'

  constructor() {
    super('Method is not implemented')
  }
}

export class ConfigurationError extends BaseError {
  name = 'ConfigurationError'

  constructor() {
    super('Configuration is missing.')
  }
}

export class PKCEGenerationError extends BaseError {
  name = 'PKCEGenerationError'

  constructor(message: string) {
    super(`PKCE Generation Error. ${message}`)
  }
}

export class LoginRequiredError extends BaseError {
  name = 'LoginRequiredError'

  constructor(message: string) {
    super(`Silent login has been failed. ${message}`)
  }
}

export class OriginIsNotAllowedError extends BaseError {
  name = 'OriginIsNotAllowedError'

  constructor(message: string) {
    super(`The origin is not allowed. ${message}`)
  }
}

export class AccessTokenRequestError extends BaseError {
  name = 'AccessTokenRequestError'

  constructor(message: string) {
    super(`Access Token Request Error. ${message}`)
  }
}

export class RefreshTokenRequestError extends BaseError {
  name = 'RefreshTokenRequestError'
  code: number

  constructor(message: string, code: number) {
    super(`Refresh Token Request Error. ${message}`)
    this.code = code
  }
}

export class HiddenIframeTimeout extends BaseError {
  name = 'HiddenIframeTimeout'

  constructor() {
    super('Iframe Timeout. Silent login is unavailable.')
  }
}

export class OidcCallbackParamsError extends BaseError {
  name = 'OidcCallbackParamsError'

  constructor() {
    super('Oidc callaback params check is failed')
  }
}

export class InvalidSessionStateError extends BaseError {
  name = 'InvalidSessionStateError'

  constructor() {
    super('This session is not valid')
  }
}

export class CryptoStorageError extends BaseError {
  name = 'CryptoStorageError'

  constructor(message: string) {
    super(`CryptoStorage read/write error ${message}`)
  }
}


export class APIError extends BaseError {
  name = 'APIError'
  code: number

  constructor(message: string, code: number) {
    super(`API request error. ${message}`)
    this.code = code
  }
}


export class AuthenticationCanceled extends BaseError {
  name = 'AuthenticationCanceled'

  constructor() {
    super('Authentication Canceled.')
  }
}

export class AuthenticationError extends BaseError {
  name = 'AuthenticationError'

  constructor(message: string) {
    super(`Authentication error. ${message}`)
  }
}

export class SessionTimedOutError extends AuthenticationError {
  constructor() {
    super('Session is timed out')
  }
}

export class AudioStreamAccessDeniedError extends BaseError {
  name = 'AudioStreamAccessDeniedError'

  constructor() {
    super('Access denied. You can\'t stream this track. Please purchase it or buy a subscription on https://subscribe.nugs.net')
  }
}

export class HTTPRequestError extends BaseError {
  name = 'HTTPRequestError'
  error?: Error | Response

  get response() {
    return this.error instanceof Response ? this.error : new Response('{}', {
      status: 400,
      statusText: 'Invalid Request'
    })
  }

  get status() {
    return this.response.status
  }

  get statusText() {
    return this.response.statusText
  }

  constructor(message: string, error?: Error | Response) {
    super(message)

    this.error = error || new Error(message)
  }
}

export class MissingStreamLinkError extends BaseError {
  name = 'MissingStreamLinkError'

  constructor() {
    super('Unable to fetch stream link or get it from cache.')
  }
}


export class PlaybackQualityChangeError extends BaseError {
  name = 'PlaybackQualityChangeError'

  constructor() {
    super('Your current subscription plan doesn\'t support a lossless hifi quality. Fallback to standard quality.')
  }
}

export class InvalidAppConfigError extends BaseError {
  name = 'InvalidAppConfigError'

  constructor() {
    super('Invalid application config')
  }
}

export class MissingTelemetryEventsError extends BaseError {
  name = 'MissingTelemetryEventsError'

  constructor() {
    super('No telemetry events were provided')
  }
}

export class MissingTelemetrySessionError extends BaseError {
  name = 'MissingTelemetrySessionError'

  constructor() {
    super('No telemetry session was provided. Please provide account and content info.')
  }
}

export class MissingEventBusError extends BaseError {
  name = 'MissingEventBusError'

  constructor() {
    super('No event bus was provided')
  }
}

export class MissingSubscriptionDataError extends BaseError {
  name = 'MissingSubscriptionDataError'

  constructor() {
    super('No subscription data was provided')
  }
}

export class BrokenTokenError extends BaseError {
  name = 'BrokenTokenError'

  constructor() {
    super('Access/Refresh token is broken')
  }
}

export class MissingTokenError extends BaseError {
  name = 'MissingTokenError'

  constructor() {
    super('No token was provided')
  }
}

export class MissingPrimaryKeyError extends BaseError {
  name = 'MissingPrimaryKeyError'

  constructor() {
    super('No primary key was provided')
  }
}
